import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConfirmationPopup,
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  PasswordComplexityNotice,
  TextInput,
} from 'shared/components';
import { copyText } from 'shared/helpers';
import { validatePasswordComplexity } from 'shared/validation';
import {
  generateLicenseUserPassword,
  sendLicenseUserPasswordSetupLink,
  setLicenseUserPassword,
} from './actions';
import './styles.scss';

const LicenseUserPasswordForm = ({
  closeCb,
  confirmCb,
  licenseUser,
  title,
}) => {
  const { t } = useTranslation();
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [isLinkConfirmDisplayed, setLinkConfirmDisplay] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  const isBusy = loading || generating || sendingLink;

  const handlePasswordChange = (val) => {
    setDirty(true);
    setPassword(val);
    setPasswordErrors(validatePasswordComplexity(val));
  };

  const validateForm = () => {
    const errors = validatePasswordComplexity(password);
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleGenerate = () => {
    setGenerating(true);
    generateLicenseUserPassword()
      .then((res) => {
        handlePasswordChange(get(res, 'data.password') || '');
        setGenerating(false);
      })
      .catch((err) => {
        setGenerating(false);
        Notification(
          'error',
          t('Password was not generated'),
          get(err, 'response.data.detail') ||
            t('There was an error while generating the password'),
        );
      });
  };

  const handleSendSetupLink = () => {
    setSendingLink(true);
    sendLicenseUserPasswordSetupLink(get(licenseUser, 'id'))
      .then((res) => {
        closeCb();
        Notification(
          'success',
          get(res, 'data.detail') || t('Password setup link sent'),
        );
      })
      .catch((err) => {
        setSendingLink(false);
        setLinkConfirmDisplay(false);
        Notification(
          'error',
          t('Password setup link was not sent'),
          get(err, 'response.data.detail') ||
            t('There was an error while sending the link'),
        );
      });
  };

  const handleCopy = () => {
    copyText(password);
    Notification('success', t('Password copied to clipboard'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isFormValid = validateForm();

    if (!isFormValid || isBusy) {
      return;
    }

    setDirty(false);
    setLoading(true);

    const data = {
      password,
    };
    const userId = get(licenseUser, 'id');

    setLicenseUserPassword(userId, data)
      .then(() => {
        confirmCb();
        Notification(
          'success',
          t('Changes saved successfully'),
          t('License user password has been changed'),
        );
      })
      .catch((err) => {
        setLoading(false);
        const passwordError = err?.response?.data?.password;
        const backendMessage =
          err?.response?.data?.message || err?.response?.data?.detail;
        if (passwordError) {
          Notification('error', t('Password validation error'), passwordError);
        } else {
          Notification(
            'error',
            t('Your changes were not saved'),
            backendMessage || t('There was an error while saving your changes'),
          );
        }
      });
  };

  const handleClose = () => {
    if (!dirty) {
      closeCb();
    } else {
      setDirtyFormAlertDisplay(true);
    }
  };

  return (
    <Modal
      title={title}
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isBusy}
      size="sm"
    >
      <form className="LicenseUserPasswordForm" onSubmit={handleSubmit}>
        <div>
          <Label text={t('Email')} inputId="email" />
          <TextInput
            id="email"
            value={get(licenseUser, 'true_email')}
            disabled
            handleChange={() => {}}
          />
        </div>
        <div>
          <Label text={t('New password')} inputId="password" />
          <TextInput
            id="password"
            value={password}
            error={passwordErrors.join(', ')}
            disabled={isBusy}
            handleChange={handlePasswordChange}
          />
          <div className="LicenseUserPasswordForm__actions">
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={isBusy}
              loading={generating}
            >
              {t('Generate')}
            </Button>
            <Button
              size="sm"
              onClick={handleCopy}
              disabled={isBusy || !password}
            >
              {t('Copy')}
            </Button>
          </div>
        </div>
        <PasswordComplexityNotice password={password} />
        <div className="LicenseUserPasswordForm__setupLink">
          <p>
            {t(
              'Or let the user choose their own password with a one-time setup link.',
            )}
          </p>
          <Button
            size="sm"
            onClick={() => setLinkConfirmDisplay(true)}
            disabled={isBusy}
          >
            {t('Send password setup link')}
          </Button>
        </div>
        <button type="submit" style={{ display: 'none' }} />
      </form>
      {isLinkConfirmDisplayed && (
        <ConfirmationPopup
          closeCb={() => setLinkConfirmDisplay(false)}
          confirmCb={handleSendSetupLink}
          title={t('Send password setup link?')}
          confirmText={t('Send link')}
          theme="success"
          disabled={sendingLink}
        >
          <strong>{get(licenseUser, 'true_email')}</strong>{' '}
          {t(
            'will get an email with a link to set their own password. No password is shown or stored.',
          )}
        </ConfirmationPopup>
      )}
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

LicenseUserPasswordForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  licenseUser: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
};

export default LicenseUserPasswordForm;
